import { IBrowserAccessor } from './browser';
import { ILogger } from './logger';
import { expect } from './expect';
import { DefaultStorageStrategy } from './storage-strategies/default-storage-strategy';
import { NDaysStorageStrategy } from './storage-strategies/n-days-storage-strategy';
import { IStorageStrategy } from './storage-strategies/storage-strategy.interface';

const STORAGE_KEY_BORDERFREE_PARAMETERS = 'GlobalE_Analytics_Borderfree';

const BF_DIRECT_TO_CHECKOUT = 'bf_direct_to_checkout';
const BF_DIRECT_TO_CHECKOUT_ENABLED = 'bf_direct_to_checkout_enabled';

export type BorderfreeHandlerConfig = {
  FT_3DA: boolean;
  FT_3DA_UTM_SOURCE_LIST?: string[];
  FT_3DA_STORAGE_LIFETIME?: number;
};

/** When it's not possible to read URL by the logic of the BorderfreeHandler
 * then URL can be provided outside as a part of BorderfreePreset. */
export type Url = string;

/** When it's not possible to read Borderfree params from URL or Storages then
 * this override can be used to provide Borderfree params directly. */
export type BorderfreeParams = {
  directToCheckout?: string;
  directToCheckoutEnabled?: string;
};

export type BorderfreePreset = Url | BorderfreeParams;

export class BorderfreeHandler {
  private readonly browser: IBrowserAccessor;
  private readonly logger: ILogger;
  private readonly FT_3DA: boolean;
  private readonly FT_3DA_STORAGE_LIFETIME: number;
  private readonly FT_3DA_UTM_SOURCE_LIST: string[];

  public directToCheckout: string;
  public directToCheckoutEnabled: string;

  /**
   * Initializes a new instance of a class that manages Borderfree parameters.
   */
  constructor(browser: IBrowserAccessor, logger: ILogger, config?: BorderfreeHandlerConfig) {
    this.browser = expect(browser).toBeDefined('The IBrowserAccessor implementation is required to construct BorderfreeHandler.');
    this.logger = expect(logger).toBeDefined('The ILogger implementation is required to construct BorderfreeHandler.');

    this.FT_3DA = config?.FT_3DA ?? true;
    this.FT_3DA_UTM_SOURCE_LIST = config?.FT_3DA_UTM_SOURCE_LIST ?? ['borderfree'];
    this.FT_3DA_STORAGE_LIFETIME = config?.FT_3DA_STORAGE_LIFETIME ?? 4320;
  }

  /**
   * Ensures Borderfree parameters are acquired from the storage, otherwise parses URL to find them
   * and saves them in both old and new ways. If no Borderfree parameters found then default value will be set.
   */
  public async handle(preset?: BorderfreePreset, utmSource?: string): Promise<void> {
    const borderfreeParams = this.getBorderfreeParams(preset);
    const borderfreeStorageStrategy = this.selectStorageStrategy(utmSource);
    const output = await borderfreeStorageStrategy.execute(borderfreeParams);
    this.directToCheckout = output.directToCheckout;
    this.directToCheckoutEnabled = output.directToCheckoutEnabled;
  }

  private getBorderfreeParams(input?: string | BorderfreePreset): BorderfreeParams {
    try {
      if (isUrl(input)) {
        // if URL with Borderfree params provided outside, then parse it assuming it contains Borderfree params
        return getFromPresetUrl(input);
      }
      if (isBorderfreeParams(input)) {
        // if object with Borderfree params provided then use it as output
        return getFromPresetObject(input);
      }

      // default flow is to parse URL or get defaults
      const url = new URL(this.browser.getPageUrl());
      const directToCheckout = url.searchParams.get(BF_DIRECT_TO_CHECKOUT);
      const directToCheckoutEnabled = url.searchParams.get(BF_DIRECT_TO_CHECKOUT_ENABLED);

      return {
        ...(directToCheckout && { directToCheckout }),
        ...(directToCheckoutEnabled && { directToCheckoutEnabled }),
      };
    } catch (error) {
      this.logger.writeErrorLog({
        method: 'BorderfreeHandler::getBorderfreeParams()',
        message: error.message,
        errorMessage: error.message,
        errorStackTrace: error.stackTrace,
      });
      // default fallback model
      return {};
    }

    function isUrl(_input: unknown): _input is string {
      return typeof _input === 'string';
    }

    function isBorderfreeParams(_input: unknown): _input is BorderfreeParams {
      return (
        typeof _input === 'object' &&
        _input !== null &&
        ('directToCheckout' in _input || 'directToCheckoutEnabled' in _input) &&
        (!('directToCheckout' in _input) || typeof (_input as BorderfreeParams).directToCheckout === 'string') &&
        (!('directToCheckoutEnabled' in _input) || typeof (_input as BorderfreeParams).directToCheckoutEnabled === 'string')
      );
    }

    function getFromPresetUrl(_input: string): BorderfreeParams {
      const url = new URLSearchParams(_input);
      const directToCheckout = url.get(BF_DIRECT_TO_CHECKOUT);
      const directToCheckoutEnabled = url.get(BF_DIRECT_TO_CHECKOUT_ENABLED);

      return {
        ...(directToCheckout && { directToCheckout }),
        ...(directToCheckoutEnabled && { directToCheckoutEnabled }),
      };
    }

    function getFromPresetObject(_input: BorderfreeParams): BorderfreeParams {
      return _input;
    }
  }

  private selectStorageStrategy(utmSource: string): IStorageStrategy<BorderfreeParams> {
    // check if Borderfree (aka "3 Days Attribution") feature is enabled and meet requirements
    // otherwise use default strategy

    if (this.FT_3DA) {
      return new NDaysStorageStrategy<BorderfreeParams>(this.browser, this.FT_3DA_STORAGE_LIFETIME, STORAGE_KEY_BORDERFREE_PARAMETERS, () =>
        this.FT_3DA_UTM_SOURCE_LIST.includes(utmSource),
      );
    }

    return new DefaultStorageStrategy(this.browser, STORAGE_KEY_BORDERFREE_PARAMETERS);
  }
}
